(() => {
  const root = document.querySelector('[data-custom-configurator]');
  if (!root || root.dataset.initialized === 'true') return;

  const form = root.querySelector('form[data-type="add-to-cart-form"]');
  const toggle = root.querySelector('[data-configurator-toggle]');
  const sticky = root.querySelector('[data-configurator-sticky]');
  const toggleActionLabel = root.querySelector('[data-configurator-action-label]');
  const panel = root.querySelector('[data-configurator-panel]');
  const checkoutButton = root.querySelector('[data-checkout-submit]');
  const cartButton = root.querySelector('[data-cart-submit]');
  const checkoutSpinner = root.querySelector('[data-checkout-spinner]');
  const status = root.querySelector('[data-configurator-status]');
  const uploadError = root.querySelector('[data-upload-error]');
  const uploadProgress = root.querySelector('[data-upload-progress]');
  const retryButton = root.querySelector('[data-upload-retry]');
  const fallback = root.querySelector('[data-js-fallback]');
  const configIdInput = root.querySelector('[data-config-id]');
  const photoInputs = Array.from(root.querySelectorAll('[data-photo-input]'));
  const steps = Array.from(root.querySelectorAll('[data-configurator-step]'));
  const cartAddUrl = root.dataset.cartAddUrl;
  const checkoutUrl = root.dataset.checkoutUrl || '/checkout';
  const successMessage = root.dataset.successMessage || 'Added to your cart. Start another custom kitten or continue shopping.';
  const maxFileSizeBytes = Number(root.dataset.maxFileSizeMb || 8) * 1024 * 1024;
  const maxTotalFileSizeBytes = Number(root.dataset.maxTotalFileSizeMb || 12) * 1024 * 1024;
  const maxImageDimension = Number(root.dataset.maxImageDimension || 1600);
  const imageQuality = Number(root.dataset.imageQuality || 0.82);

  if (!form || !toggle || !panel || !checkoutButton || !cartButton) return;

  if (sticky && document.body && sticky.parentElement !== document.body) {
    document.body.appendChild(sticky);
  }

  root.dataset.initialized = 'true';
  root.dataset.jsReady = 'true';
  root.classList.remove('is-js-failed');
  if (fallback) fallback.hidden = true;

  let busy = false;
  let retryAction = '';
  const objectUrls = new Map();
  let toggleInView = true;
  let stickyObserver = null;

  const createConfigId = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'custom-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
  };

  const setStatus = (message, isError = false) => {
    if (!status) return;
    status.textContent = message || '';
    status.hidden = !message;
    status.classList.toggle('kv-custom-configurator__status--error', isError);
  };

  const setUploadError = (message) => {
    if (!uploadError) return;
    uploadError.textContent = message || '';
    uploadError.hidden = !message;
  };

  const setUploadProgress = (message, isError = false) => {
    if (!uploadProgress) return;
    uploadProgress.textContent = message || '';
    uploadProgress.hidden = !message;
    uploadProgress.classList.toggle('kv-custom-configurator__upload-progress--error', isError);
  };

  const setRetryAvailable = (action = '') => {
    retryAction = action;
    if (!retryButton) return;
    retryButton.hidden = !action;
    retryButton.disabled = false;
  };

  const getPhotoTotalBytes = () =>
    photoInputs.reduce((total, input) => total + ((input.files && input.files[0])?.size || 0), 0);

  const getFileKey = (file) => [file.name, file.size, file.lastModified].join(':');

  const replaceInputFile = (input, file) => {
    try {
      if (typeof DataTransfer === 'undefined') return false;
      const transfer = new DataTransfer();
      transfer.items.add(file);
      input.files = transfer.files;
      return Boolean(input.files && input.files[0]);
    } catch (error) {
      return false;
    }
  };

  const compressImage = (file) =>
    new Promise((resolve, reject) => {
      const sourceUrl = URL.createObjectURL(file);
      const image = new Image();

      image.onload = () => {
        const scale = Math.min(1, maxImageDimension / Math.max(image.naturalWidth, image.naturalHeight));
        const width = Math.max(1, Math.round(image.naturalWidth * scale));
        const height = Math.max(1, Math.round(image.naturalHeight * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');

        if (!context || typeof canvas.toBlob !== 'function') {
          URL.revokeObjectURL(sourceUrl);
          resolve(file);
          return;
        }

        context.drawImage(image, 0, 0, width, height);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(sourceUrl);
          if (!blob) {
            resolve(file);
            return;
          }

          const keepOriginal = blob.size >= file.size &&
            image.naturalWidth <= maxImageDimension &&
            image.naturalHeight <= maxImageDimension;
          if (keepOriginal) {
            resolve(file);
            return;
          }

          const baseName = file.name.replace(/\.[^/.]+$/, '') || 'pet-photo';
          resolve(new File([blob], baseName + '.jpg', { type: 'image/jpeg', lastModified: Date.now() }));
        }, 'image/jpeg', imageQuality);
      };

      image.onerror = () => {
        URL.revokeObjectURL(sourceUrl);
        reject(new Error('Unable to read image'));
      };

      image.src = sourceUrl;
    });

  const compressInputFile = async (input) => {
    const file = input.files && input.files[0];
    if (!file) return;
    const fileKey = getFileKey(file);
    if (input.dataset.prepared === fileKey) return;

    setUploadProgress('Optimising your photos...');
    try {
      const compressedFile = await compressImage(file);
      replaceInputFile(input, compressedFile);
      input.dataset.prepared = getFileKey(input.files && input.files[0] ? input.files[0] : file);
      updatePreview(input);
    } finally {
      if (photoInputs.every((photoInput) => {
        const selectedFile = photoInput.files && photoInput.files[0];
        return !selectedFile || photoInput.dataset.prepared === getFileKey(selectedFile);
      })) {
        setUploadProgress('Photos ready to upload.');
      }
    }
  };

  const prepareSelectedPhotos = async () => {
    for (const input of photoInputs) {
      if (input.files && input.files[0]) await compressInputFile(input);
    }
    if (!validatePhotos(false, false)) throw new Error('Photo validation failed');
  };

  const setStep = (activeIndex) => {
    steps.forEach((step, index) => {
      step.classList.toggle('kv-custom-configurator__step--active', index === activeIndex);
    });
  };

  const clearObjectUrl = (input) => {
    const previousUrl = objectUrls.get(input);
    if (previousUrl) {
      URL.revokeObjectURL(previousUrl);
      objectUrls.delete(input);
    }
  };

  const updatePreview = (input) => {
    const slot = input.closest('[data-photo-slot]');
    if (!slot) return;
    const preview = slot.querySelector('[data-photo-preview]');
    const image = slot.querySelector('[data-photo-preview-image]');
    const name = slot.querySelector('[data-photo-name]');
    const file = input.files && input.files[0];

    clearObjectUrl(input);
    slot.classList.toggle('has-file', Boolean(file));
    if (!file) {
      if (preview) preview.hidden = true;
      if (image) {
        image.removeAttribute('src');
        image.removeAttribute('alt');
      }
      if (name) name.textContent = 'Choose an image';
      return;
    }

    const url = URL.createObjectURL(file);
    objectUrls.set(input, url);
    if (preview) preview.hidden = false;
    if (image) {
      image.src = url;
      image.alt = file.name;
    }
    if (name) name.textContent = file.name;
  };

  const validatePhotos = (focusInvalid = false, allowUnprepared = false) => {
    let valid = true;
    let firstInvalid = null;
    setUploadError('');

    photoInputs.forEach((input, index) => {
      const file = input.files && input.files[0];
      let message = '';

      if (!file && index === 0) {
        message = 'Please add a clear front-facing photo.';
      } else if (file && !file.type.startsWith('image/')) {
        message = 'Please choose an image file.';
      } else if (file && file.size > maxFileSizeBytes) {
        const isPrepared = input.dataset.prepared === getFileKey(file);
        if (!allowUnprepared || isPrepared) {
          message = 'Each photo must be smaller than ' + root.dataset.maxFileSizeMb + ' MB after optimisation.';
        }
      }

      input.setCustomValidity(message);
      input.setAttribute('aria-invalid', message ? 'true' : 'false');
      const slot = input.closest('[data-photo-slot]');
      if (slot) slot.classList.toggle('has-error', Boolean(message));
      if (message && !firstInvalid) firstInvalid = input;
      if (message) valid = false;
      updatePreview(input);
    });

    const hasUnpreparedPhoto = photoInputs.some((input) => {
      const file = input.files && input.files[0];
      return file && input.dataset.prepared !== getFileKey(file);
    });
    const totalTooLarge = getPhotoTotalBytes() > maxTotalFileSizeBytes &&
      (!allowUnprepared || !hasUnpreparedPhoto);

    if (totalTooLarge) {
      valid = false;
      setUploadError('Your photos are too large together. Please remove one or choose smaller images.');
      if (focusInvalid && firstInvalid) firstInvalid.focus();
    } else if (!valid) {
      setUploadError('Please add the required front-facing photo and check the file format or size.');
      if (focusInvalid && firstInvalid) firstInvalid.focus();
    }

    return valid;
  };

  const setBusy = (value, mode) => {
    busy = value;
    root.classList.toggle('is-busy', value);
    checkoutButton.disabled = value;
    cartButton.disabled = value;
    checkoutButton.setAttribute('aria-disabled', value ? 'true' : 'false');
    cartButton.setAttribute('aria-disabled', value ? 'true' : 'false');
    if (checkoutSpinner) checkoutSpinner.hidden = !value || mode !== 'checkout';
    if (!value) {
      checkoutButton.removeAttribute('aria-disabled');
      cartButton.removeAttribute('aria-disabled');
    }
  };

  const clearPhotos = () => {
    photoInputs.forEach((input) => {
      clearObjectUrl(input);
      input.value = '';
      input.setCustomValidity('');
      input.setAttribute('aria-invalid', 'false');
      const slot = input.closest('[data-photo-slot]');
      if (slot) slot.classList.remove('has-error');
      delete input.dataset.prepared;
      updatePreview(input);
    });
    setUploadError('');
  };

  const resetForNextKitten = () => {
    clearPhotos();
    const note = form.querySelector('textarea[name="properties[Custom note]"]');
    if (note) note.value = '';
    if (configIdInput) configIdInput.value = createConfigId();
    setBusy(false);
    setStep(0);
    setStatus(successMessage);
    setUploadProgress('');
    setRetryAvailable('');
  };

  if (configIdInput) configIdInput.value = createConfigId();
  setStep(0);

  const syncSticky = () => {
    if (!sticky) return;
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    sticky.hidden = isOpen || toggleInView;
  };

  const updateStickyFallback = () => {
    const rect = toggle.getBoundingClientRect();
    toggleInView = rect.bottom > 64 && rect.top < window.innerHeight - 32;
    syncSticky();
  };

  if (sticky) {
    if (typeof IntersectionObserver === 'function') {
      stickyObserver = new IntersectionObserver((entries) => {
        toggleInView = Boolean(entries[0] && entries[0].isIntersecting);
        syncSticky();
      }, { threshold: 0.01, rootMargin: '-64px 0px -32px 0px' });
      stickyObserver.observe(toggle);
    } else {
      updateStickyFallback();
      window.addEventListener('scroll', updateStickyFallback, { passive: true });
      window.addEventListener('resize', updateStickyFallback);
    }

    sticky.addEventListener('click', () => {
      if (toggle.getAttribute('aria-expanded') !== 'true') toggle.click();
      window.setTimeout(() => {
        toggle.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'center'
        });
      }, 0);
    });
  }
  syncSticky();

  toggle.addEventListener('click', () => {
    const open = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
    panel.hidden = open;
    root.classList.toggle('is-open', !open);
    if (toggleActionLabel) toggleActionLabel.textContent = open ? 'Start' : 'Close';
    if (!open) setStep(0);
    if (!open) {
      const firstControl = panel.querySelector('input, select, textarea, button');
      if (firstControl) window.setTimeout(() => firstControl.focus(), 0);
    }
    syncSticky();
  });

  photoInputs.forEach((input) => {
    input.addEventListener('invalid', () => {
      const optionalPhotos = input.closest('.kv-custom-configurator__optional-photos');
      if (optionalPhotos) optionalPhotos.open = true;
    });
    input.addEventListener('change', async () => {
      setRetryAvailable('');
      validatePhotos(false, true);
      if (photoInputs.some((photoInput) => photoInput.files && photoInput.files[0])) setStep(1);
      setStatus('');

      try {
        await compressInputFile(input);
        validatePhotos(false, false);
      } catch (error) {
        setUploadProgress('We could not prepare this photo. Please choose it again.', true);
      }
    });
  });

  const getCartElement = () => document.querySelector('cart-notification') || document.querySelector('cart-drawer');

  const addToCartInBackground = async () => {
    const cart = getCartElement();
    const formData = new FormData(form);

    if (cart && typeof cart.getSectionsToRender === 'function') {
      const sections = cart.getSectionsToRender().map((section) => section.id).filter(Boolean);
      if (sections.length) formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
    }

    const response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });
    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Unable to add the item');
    }

    if (cart && typeof cart.renderContents === 'function' && result.sections) {
      cart.renderContents(result);
    }

    return result;
  };

  form.addEventListener('submit', async (event) => {
    if (busy || !validatePhotos(false, true)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!busy) validatePhotos(true, true);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    setBusy(true, 'cart');
    setRetryAvailable('');
    setUploadProgress('Preparing your photos...');

    try {
      await prepareSelectedPhotos();
      if (!form.checkValidity()) throw new Error('Form validation failed');
      setStep(2);
      setUploadProgress('Uploading your photos...');
      await addToCartInBackground();
      resetForNextKitten();
    } catch (error) {
      setBusy(false);
      setUploadProgress('We could not add this custom kitten. Please try again.', true);
      setRetryAvailable('cart');
    }
  }, true);

  checkoutButton.addEventListener('click', async () => {
    if (busy || !validatePhotos(true, true)) {
      if (!busy) form.reportValidity();
      return;
    }

    setBusy(true, 'checkout');
    setRetryAvailable('');
    setStep(2);
    setUploadProgress('Preparing your photos...');

    try {
      await prepareSelectedPhotos();
      if (!form.checkValidity()) throw new Error('Form validation failed');
      setUploadProgress('Uploading your photos...');
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: new FormData(form),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Unable to add the item');
      }
      setRetryAvailable('');
      window.location.assign(checkoutUrl);
    } catch (error) {
      setBusy(false);
      setUploadProgress('We could not start checkout. Please try again.', true);
      setRetryAvailable('checkout');
    }
  });

  if (retryButton) {
    retryButton.addEventListener('click', () => {
      const action = retryAction;
      setRetryAvailable('');
      if (action === 'checkout') checkoutButton.click();
      if (action === 'cart') form.requestSubmit(cartButton);
    });
  }

  window.addEventListener('pagehide', () => {
    if (stickyObserver) stickyObserver.disconnect();
    objectUrls.forEach((url) => URL.revokeObjectURL(url));
    objectUrls.clear();
  }, { once: true });
})();

